import { Card, Modal, Button, Divider } from 'antd';
import {
	Chart as Chartjs,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import NativeNumber from '../../component/Numeral';
import {
	investmentWithdrawals,
	approveWithdrawal,
	processWithdrawal,
	rejectWithdrawal,
} from '../../methods/redux/reducers/investment';
import remakeQueryString from '../../methods/utils/remake-query-string';

Chartjs.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);

const SingleInvestmentDetailsStyle = styled.div`
	& .locked-indicator {
		padding: 3px;
		color: white;
		text-align: center;
		font-size: 12px;
		font-weight: bold;
		background: red;
	}

	& .iscollateral-indicator {
		position: relative;
		top: -25px;
		padding: 4px;
		margin: auto;
		color: white;
		text-align: center;
		font-size: 13.5px;
		font-weight: bold;
		background: orange;
		width: 75%;
	}

	& .title {
		text-transform: capitalize;
	}

	& .profile-details {
		& > div {
			display: inline-block;
			padding: 5px;
			margin-right: 5px;

			& .label {
				font-size: 12px;
				color: gray;
			}
		}
	}
`;

const SingleInvestmentWithdrawal = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [visible, setVisible] = useState(false);
	const [visibleTopUp, setVisibleTopUp] = useState(false);
	const [withdrawalVisible, setWithdrawalVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const { withdrawalList } = useAppSelector((store) => store.investment);

	const investmentWithdrawalId = searchParams.get('investmentWithdrawalId');
	const searchParamsObject = Object.fromEntries(searchParams);
	interface Investment {
		_id: any;
		title: string;
		investmentForm: string;
		durationInMonth: number;
		investmentStatus: string;
		reInvestAfterMaturity: string;
		riseInvestmentId: string;
		status: string;
		totalDeposit: number;
		totalProfit: number;
		currencyId: any;
		totalWithdrawn: number;
		createdAt: string;
		updatedAt: string;
		riseVestData: any;
		userId: any;
	}
	const investment = withdrawalList?.investmentId as Investment;
	console.info('withdrawalList', withdrawalList);

	const handleSubmitApprove = async () => {
		try {
			setLoading(true);
			await dispatch(approveWithdrawal({ investmentWithdrawalId }));
			dispatch(
				investmentWithdrawals({
					investmentWithdrawalId,
				})
			);
			setLoading(false);
		} catch (error) {
			setLoading(false);
		} finally {
			setLoading(false);
			setVisible(false);
		}
	};

	const handleSubmitProcess = async () => {
		setLoading(true);
		try {
			await dispatch(processWithdrawal({ investmentWithdrawalId }));
			dispatch(
				investmentWithdrawals({
					investmentWithdrawalId,
				})
			);
			setLoading(false);
		} catch (error) {
			setLoading(false);
		} finally {
			setVisibleTopUp(false);
			setLoading(false);
		}
	};

	const handleSubmitReject = async () => {
		setLoading(true);
		try {
			await dispatch(rejectWithdrawal({ investmentWithdrawalId }));
			dispatch(
				investmentWithdrawals({
					investmentWithdrawalId,
				})
			);
			setLoading(false);
		} catch (error) {
			setLoading(false);
		} finally {
			setWithdrawalVisible(false);
			setLoading(false);
		}
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		const updateQueryString = (query: any, action = 'update') => {
			remakeQueryString(query, searchParams, setSearchParams, action);
		};
		updateQueryString({
			investmentWithdrawalId: searchParamsObject.investmentWithdrawalId,
		});
		if (Object.keys(queryData).length > 0) {
			dispatch(investmentWithdrawals({ ...queryData }));
		}
	}, []);

	const withdrawalStatusColor = (status: any) => {
		switch (status) {
			case 'cancelled':
				return 'gray';

			case 'completed':
				return 'var(--green-color)';

			case 'processing':
				return 'blue';

			case 'pending':
				return 'orange';

			case 'review':
				return 'yellow';

			case 'rejected':
				return 'var(--red-color)';

			default:
				return 'var(--black-color)';
		}
	};

	return (
		<SingleInvestmentDetailsStyle>
			<Card style={{ position: 'relative' }}>
				<Button
					onClick={() => window.history.back()}
					style={{ padding: '10px', cursor: 'pointer' }}
				>
					Back
				</Button>
				<br />

				<div style={{ display: 'flex', gap: '4px' }}>
					<h1 className="title">
						{withdrawalList?.investmentId
							? withdrawalList?.investmentId?.title
							: 'No Title'}{' '}
						for
					</h1>
					{withdrawalList?.userId && (
						<Link to={`/user-profile/${withdrawalList?.userId?._id}`}>
							<h1 className="title" style={{ color: '#1890ff' }}>
								{withdrawalList?.userId?.username ? (
									<span>@{withdrawalList?.userId?.username}</span>
								) : (
									withdrawalList.userId?.email
								)}
							</h1>
						</Link>
					)}
				</div>

				<p className="label">Title</p>
				<br />

				{withdrawalList?.withdrawalStatus && (
					<h2>
						Withdrawal Status:{' '}
						<span
							style={{
								color: withdrawalStatusColor(withdrawalList?.withdrawalStatus),
							}}
						>
							{withdrawalList?.withdrawalStatus}
						</span>
					</h2>
				)}
				<br />

				<div className="profile-details">
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										withdrawalList?.amount ? withdrawalList?.amount : '0.00'
									}
									decimalPlaces={4}
								/>{' '}
							</strong>
						</p>
						<p className="label">Amount</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										withdrawalList?.calculatedFee?.discount
											? withdrawalList?.calculatedFee?.discount
											: '0.00'
									}
									decimalPlaces={4}
								/>{' '}
							</strong>
						</p>
						<p className="label">Discount</p>
					</div>
					<div>
						<p>
							{withdrawalList?.calculatedFee?.discountApplied
								? 'True'
								: 'False'}
						</p>
						<p className="label">Discount Applied</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										withdrawalList?.calculatedFee?.fee
											? withdrawalList?.calculatedFee?.fee
											: '0.00'
									}
									decimalPlaces={4}
								/>{' '}
							</strong>
						</p>
						<p className="label">Fee</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										withdrawalList?.calculatedFee?.feeInUsd
											? withdrawalList?.calculatedFee?.feeInUsd
											: '0.00'
									}
									decimalPlaces={4}
								/>{' '}
							</strong>
						</p>
						<p className="label">Fee In Usd</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										withdrawalList?.calculatedFee?.finalAmount
											? withdrawalList?.calculatedFee?.finalAmount
											: '0.00'
									}
									decimalPlaces={4}
								/>{' '}
							</strong>
						</p>
						<p className="label">Final Amount</p>
					</div>
					<div>
						<p>{withdrawalList?.calculatedFee?.type}</p>
						<p className="label">Type</p>
					</div>
					<div>
						<p>
							{withdrawalList?.calculatedFee?.xendAmount
								? withdrawalList?.calculatedFee?.xendAmount
								: '--'}
						</p>
						<p className="label">Xend Amount</p>
					</div>
					<div>
						<p>
							{withdrawalList?.calculatedFee?.xendDiscount
								? withdrawalList?.calculatedFee?.xendDiscount
								: '--'}
						</p>
						<p className="label">Xend Discount</p>
					</div>
					<div>
						<p>{withdrawalList?.investmentForm}</p>
						<p className="label">Investment Form</p>
					</div>
					<div>
						<p>
							{withdrawalList?.riseVestTransactionId
								? withdrawalList?.riseVestTransactionId
								: '--'}
						</p>
						<p className="label">RiseVest TransactionId</p>
					</div>

					<div>
						<p>
							{withdrawalList?.status ? withdrawalList?.status : 'No Status'}
						</p>
						<p className="label">Status</p>
					</div>

					{withdrawalList?.withdrawalStatus && (
						<div>
							<p
								style={{
									color: withdrawalStatusColor(
										withdrawalList?.withdrawalStatus
									),
								}}
							>
								{withdrawalList?.withdrawalStatus}
							</p>
							<p className="label">Withdrawal Status</p>
						</div>
					)}

					<div>
						<p>
							{withdrawalList?.investmentId
								? moment(
										withdrawalList?.investmentId
											? withdrawalList?.createdAt
											: 'No createdAt'
								  ).format('MMM DD, YY / hh:mm:ss a')
								: '--'}
						</p>
						<p className="label">Created Date</p>
					</div>
					<div>
						<p>
							{moment(
								withdrawalList?.investmentId
									? withdrawalList?.updatedAt
									: 'No updatedAt'
							).format('MMM DD, YY / hh:mm:ss a')}
						</p>
						<p className="label">Updated Date</p>
					</div>
				</div>

				<Divider />

				<h2>
					<strong>Withdrawal Actions:</strong>
				</h2>
				<div style={{ display: 'flex', gap: '8px' }}>
					<Button
						type="primary"
						onClick={() => setVisible(true)}
						disabled={
							loading ||
							withdrawalList?.withdrawalStatus === 'completed' ||
							withdrawalList?.withdrawalStatus !== 'review'
						}
					>
						Approve withdrawal
					</Button>
					<Button
						disabled={
							loading ||
							withdrawalList?.withdrawalStatus === 'completed' ||
							withdrawalList?.withdrawalStatus !== 'pending'
						}
						type="primary"
						onClick={() => setVisibleTopUp(true)}
					>
						Process withdrawal
					</Button>
					<Button
						disabled={
							loading ||
							withdrawalList?.withdrawalStatus === 'completed' ||
							!(
								withdrawalList?.withdrawalStatus === 'pending' ||
								withdrawalList?.withdrawalStatus === 'review'
							)
						}
						type="primary"
						onClick={() => setWithdrawalVisible(true)}
					>
						Reject withdrawal{' '}
					</Button>
				</div>
				<br />

				<Divider />
				<Link
					title="Investment Topups"
					className="out-links"
					to={`/core-modules/investment/users/single?investmentId=${investment?._id}
`}
				>
					Go To Investment
				</Link>
			</Card>

			<Modal
				okText="Approve Withdrawal"
				title="Approve Withdrawal"
				onOk={handleSubmitApprove}
				open={visible} // ✅ Fix deprecated `visible` prop
				onCancel={() => setVisible(false)}
			>
				<p>Approve this withdrawal?</p>
			</Modal>

			<Modal
				okText="Process Withdrawal"
				title="Process Withdrawal"
				onOk={handleSubmitProcess}
				visible={visibleTopUp}
				onCancel={() => setVisibleTopUp(false)}
			>
				<p>Process this withdrawal?</p>
			</Modal>

			<Modal
				okText="Reject Withdrawal"
				title="Reject Withdrawal"
				onOk={handleSubmitReject}
				visible={withdrawalVisible}
				onCancel={() => setWithdrawalVisible(false)}
			>
				<p>Reject this withdrawal?</p>
			</Modal>
		</SingleInvestmentDetailsStyle>
	);
};

export default SingleInvestmentWithdrawal;
